import React from "react";
import dayjs from "dayjs";
import {
    Box, Flex, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import PaymentProviderChip from "./PaymentProviderChip";
import SchemeBadge from "./SchemeBadge";

const PAID_SCHEME = { bg: "rgba(76, 175, 80, 0.15)", color: "rgba(129, 199, 132, 1)", border: "rgba(76, 175, 80, 0.4)" };
const UNPAID_SCHEME = { bg: "rgba(158, 158, 158, 0.15)", color: "rgba(189, 189, 189, 1)", border: "rgba(158, 158, 158, 0.4)" };
const REFUND_SCHEME = { bg: "rgba(255, 87, 34, 0.15)", color: "rgba(255, 138, 101, 1)", border: "rgba(255, 87, 34, 0.4)" };

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDate(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Client cell with name + email subtitle.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function ClientCell({ payment }) {
    const nameColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const name = [payment?.name, payment?.lastname].filter(Boolean).join(" ") || "—";
    return (
        <Stack spacing="2px" minW="0">
            <Text noOfLines={1} fontSize="0.78rem" fontWeight="600" color={nameColor}>{name}</Text>
            <Text noOfLines={1} fontSize="0.7rem" color={subColor}>{payment?.email || ""}</Text>
        </Stack>
    );
}

/**
 * Status cell: paid / refunded / pending badge stack.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function StatusCell({ payment }) {
    return (
        <Flex gap="4px" wrap="wrap">
            {payment?.paid ? <SchemeBadge scheme={PAID_SCHEME} label="Payé" /> : <SchemeBadge scheme={UNPAID_SCHEME} label="Non payé" />}
            {payment?.refund && <SchemeBadge scheme={REFUND_SCHEME} label="Remboursé" />}
        </Flex>
    );
}

/**
 * Renders the table header row.
 * @returns {React.ReactElement}
 */
function BrowseHead() {
    return (
        <Thead>
            <Tr><Th>Date</Th><Th>Référence</Th><Th>Client</Th><Th>Type</Th><Th>Montant</Th><Th>Statut</Th></Tr>
        </Thead>
    );
}

/**
 * Renders a single payment row.
 * @param {Object} props - { payment }
 * @returns {React.ReactElement}
 */
function PaymentRow({ payment }) {
    const monoColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    return (
        <Tr>
            <Td>{formatDate(payment?.created_at)}</Td>
            <Td>
                <Stack spacing="2px">
                    <Text noOfLines={1} fontFamily="mono" fontSize="0.72rem" color={monoColor}>{payment?.reference || "—"}</Text>
                    <PaymentProviderChip payment={payment} />
                </Stack>
            </Td>
            <Td><ClientCell payment={payment} /></Td>
            <Td>{payment?.type || "—"}</Td>
            <Td>{payment?.total_amount != null ? payment.total_amount : "—"}</Td>
            <Td><StatusCell payment={payment} /></Td>
        </Tr>
    );
}

/**
 * Renders a centered state box for loading / error / empty states.
 * @param {Object} props - { children }
 * @returns {React.ReactElement}
 */
function StateBox({ children }) {
    return <Box textAlign="center" py="40px">{children}</Box>;
}

/**
 * Selects the body component based on loading / error / empty / data state.
 * @param {Object} props - { items, loading, error }
 * @returns {React.ReactElement}
 */
function BrowseBody({ items, loading, error }) {
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (loading && (!items || items.length === 0)) return <StateBox><Spinner color="brand.500" /></StateBox>;
    if (error && (!items || items.length === 0)) {
        return <StateBox><Text color="red.500" fontSize="sm">{typeof error === "string" ? error : "Erreur lors du chargement"}</Text></StateBox>;
    }
    if (!items || items.length === 0) return <StateBox><Text color={emptyColor} fontSize="sm">Aucun paiement à afficher</Text></StateBox>;
    return (
        <TableContainer>
            <Table size="sm">
                <BrowseHead />
                <Tbody>{items.map((p) => <PaymentRow key={p.id} payment={p} />)}</Tbody>
            </Table>
        </TableContainer>
    );
}

/**
 * Browse table for the "Paiements" tab. Handles loading / error / empty internally.
 * @param {Object} props - { items, loading, error }
 * @returns {React.ReactElement}
 */
function PaymentsBrowseTable({ items, loading, error }) {
    return <BrowseBody items={items} loading={loading} error={error} />;
}

export default PaymentsBrowseTable;
